/**
 * EncycGames platform subscriptions — yearly plans (defaults).
 * Live offers are stored in DB and editable in Admin → Subscriptions.
 * Player/Parents: public profile for Football, Cricket, Badminton.
 * Academies & Schools: custom — contact us.
 */

export const PLAYER_INCLUDED_SPORTS = ['Football', 'Cricket', 'Badminton'] as const;

/** Fallback defaults when API is unavailable */
export const DEFAULT_EARLY_BIRD_DISCOUNT_PERCENT = 60;
export const DEFAULT_PLAYER_YEARLY_LIST_USD = 200;

export const EARLY_BIRD_DISCOUNT_PERCENT = DEFAULT_EARLY_BIRD_DISCOUNT_PERCENT;
export const PLAYER_YEARLY_LIST_USD = DEFAULT_PLAYER_YEARLY_LIST_USD;
export const PLAYER_YEARLY_EARLY_BIRD_USD =
  DEFAULT_PLAYER_YEARLY_LIST_USD * (1 - DEFAULT_EARLY_BIRD_DISCOUNT_PERCENT / 100);

export const DEFAULT_OFFER_HEADLINE =
  'Improve Foundation Skills and Get discount*';
export const DEFAULT_TERMS_NOTE = '*Terms & conditions apply.';

export type SubscriptionTierId = 'players_parents' | 'academies' | 'schools' | 'corporate' | 'coaches';

export interface SubscriptionOffer {
  id: SubscriptionTierId;
  name: string;
  audience: string;
  billingPeriod: 'yearly';
  currency: string;
  /** Fixed yearly USD, or null when custom / contact sales. */
  yearlyUsd: number | null;
  earlyBirdPercent: number;
  earlyBirdEnabled: boolean;
  /** Max early-bird slots (e.g. first 200 academies). Null = unlimited. */
  earlyBirdLimit?: number | null;
  earlyBirdClaimed?: number;
  earlyBirdRemaining?: number | null;
  customPricing: boolean;
  sports: string[];
  features: string[];
  includesNote?: string;
  offerHeadline?: string;
  termsNote?: string;
  ctaLabel: string;
  ctaPath: string;
  highlight?: boolean;
  updatedAt?: string;
}

export type SubscriptionTier = SubscriptionOffer;

export const DEFAULT_SUBSCRIPTION_OFFERS: SubscriptionOffer[] = [
  {
    id: 'players_parents',
    name: 'Players / Parents',
    audience: 'Individual athletes and families',
    billingPeriod: 'yearly',
    currency: 'USD',
    yearlyUsd: DEFAULT_PLAYER_YEARLY_LIST_USD,
    earlyBirdPercent: DEFAULT_EARLY_BIRD_DISCOUNT_PERCENT,
    earlyBirdEnabled: true,
    earlyBirdLimit: null,
    earlyBirdClaimed: 0,
    earlyBirdRemaining: null,
    customPricing: false,
    sports: [...PLAYER_INCLUDED_SPORTS],
    ctaLabel: 'Register free',
    ctaPath: '/register/player',
    highlight: true,
    includesNote: 'Public profile · 3 sports · yearly',
    offerHeadline: DEFAULT_OFFER_HEADLINE,
    termsNote: DEFAULT_TERMS_NOTE,
    features: [
      'Public player profile for Football, Cricket & Badminton',
      'Structured learning (age-wise)',
      '4 assessments per year — unused assessments carry forward',
      'Self & coach assessment',
      'Technical Foundation contests',
      'Certificates & progress tracking',
      'Leaderboard visibility',
    ],
  },
  {
    id: 'academies',
    name: 'Academies',
    audience: 'Sports academies and clubs',
    billingPeriod: 'yearly',
    currency: 'USD',
    yearlyUsd: null,
    earlyBirdPercent: 70,
    earlyBirdEnabled: true,
    earlyBirdLimit: 200,
    earlyBirdClaimed: 0,
    earlyBirdRemaining: 200,
    customPricing: true,
    sports: [...PLAYER_INCLUDED_SPORTS],
    ctaLabel: 'Contact sales',
    ctaPath: '/subscriptions/contact?plan=academies',
    includesNote: 'First 200 academies · 70% early bird*',
    offerHeadline: 'First 200 academies — 70% early bird*',
    termsNote: '*Terms & conditions apply. Offer varies by size; contact us for a yearly quote.',
    features: [
      'Public academy profile',
      'Learning library',
      'Curriculum & pathways',
      'Leaderboard',
      '4 assessments per year per athlete — unused assessments carry forward',
      'Assessment workflows',
      'Tournament management',
      'Sparring / pairing',
      'Multi-sport coverage',
    ],
  },
  {
    id: 'schools',
    name: 'Schools/Colleges',
    audience: 'Schools, colleges, and education institutions',
    billingPeriod: 'yearly',
    currency: 'USD',
    yearlyUsd: null,
    earlyBirdPercent: 70,
    earlyBirdEnabled: true,
    earlyBirdLimit: 50,
    earlyBirdClaimed: 0,
    earlyBirdRemaining: 50,
    customPricing: true,
    sports: [...PLAYER_INCLUDED_SPORTS],
    ctaLabel: 'Contact sales',
    ctaPath: '/subscriptions/contact?plan=schools',
    includesNote: 'First 50 schools · 70% early bird*',
    offerHeadline: 'First 50 schools — 70% early bird*',
    termsNote: '*Terms & conditions apply. Offer varies by size; contact us for a yearly quote.',
    features: [
      'Campus-wide player onboarding',
      '4 assessments per year per athlete — unused assessments carry forward',
      'Learning & assessment for PE / sports',
      'Contest & event coordination',
      'Reporting for coaches and admins',
      'Multi-sport options',
      'Custom training & support',
    ],
  },
  {
    id: 'corporate',
    name: 'Corporate',
    audience: 'Companies and large organizations',
    billingPeriod: 'yearly',
    currency: 'USD',
    yearlyUsd: null,
    earlyBirdPercent: 0,
    earlyBirdEnabled: false,
    earlyBirdLimit: null,
    earlyBirdClaimed: 0,
    earlyBirdRemaining: null,
    customPricing: true,
    sports: [...PLAYER_INCLUDED_SPORTS],
    ctaLabel: 'Contact sales',
    ctaPath: '/subscriptions/contact?plan=corporate',
    includesNote: 'Bulk seats · multi-sport · yearly',
    offerHeadline: 'Corporate packages — bulk seats across sports',
    termsNote:
      '*Terms & conditions apply. Corporate packages are tailored after a short discovery call — no payment is collected until your agreement is confirmed.',
    features: [
      'Bulk seats for employees, clubs, or partner programs',
      'Central admin for onboarding and seat management',
      'Contests across sports under one corporate subscription',
      'Company-wide and sport-wise leaderboards',
      '4 assessments per year per athlete — unused assessments carry forward',
      'Certificates, progress reporting, and coach workflows',
      'Custom branding and reporting options (by agreement)',
    ],
  },
];

/** @deprecated use DEFAULT_SUBSCRIPTION_OFFERS — kept for existing imports */
export const SUBSCRIPTION_TIERS = DEFAULT_SUBSCRIPTION_OFFERS;

const CURRENCY_LOCALES: Record<string, string> = {
  USD: 'en-US',
  INR: 'en-IN',
  GBP: 'en-GB',
  AED: 'en-AE',
  SGD: 'en-SG',
};

/** Format a yearly catalog amount in the country's currency. */
export function formatCatalogYearly(amount: number, currency = 'USD'): string {
  const code = String(currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[code] || 'en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'INR' ? 0 : amount % 1 === 0 ? 0 : 2,
    }).format(Number(amount) || 0);
  } catch {
    return `${Number(amount) || 0} ${code}`;
  }
}

export function formatUsdYearly(amount: number): string {
  return formatCatalogYearly(amount, 'USD');
}

export function earlyBirdPrice(listUsd: number, percent: number): number {
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  return Math.round(listUsd * (1 - p / 100) * 100) / 100;
}

export function resolvePlayerOffer(
  offers: SubscriptionOffer[] = DEFAULT_SUBSCRIPTION_OFFERS,
): SubscriptionOffer {
  return offers.find((o) => o.id === 'players_parents') || DEFAULT_SUBSCRIPTION_OFFERS[0];
}

/** Shared resolver for payment / checkout / dependent modules. */
export function getEffectiveYearlyPrice(offer: SubscriptionOffer): {
  listUsd: number | null;
  payableUsd: number | null;
  discountPercent: number;
  earlyBirdActive: boolean;
} {
  if (offer.customPricing || offer.yearlyUsd == null) {
    return { listUsd: null, payableUsd: null, discountPercent: 0, earlyBirdActive: false };
  }
  const earlyBirdActive = Boolean(offer.earlyBirdEnabled && offer.earlyBirdPercent > 0);
  const discountPercent = earlyBirdActive ? offer.earlyBirdPercent : 0;
  return {
    listUsd: offer.yearlyUsd,
    payableUsd: earlyBirdActive
      ? earlyBirdPrice(offer.yearlyUsd, offer.earlyBirdPercent)
      : offer.yearlyUsd,
    discountPercent,
    earlyBirdActive,
  };
}

export function suggestedOfferIdForRole(
  role?: string | null,
  academyType?: string | null,
): SubscriptionTierId | null {
  const r = String(role || '').toLowerCase();
  if (r === 'player') return 'players_parents';
  if (r === 'coach') return 'coaches';
  if (r === 'academy_admin') {
    const t = String(academyType || '').toLowerCase();
    if (t === 'regular_school' || t === 'college') return 'schools';
    if (t === 'corporate') return 'corporate';
    return 'academies';
  }
  return null;
}

export function isPurchasableOffer(offer: SubscriptionOffer): boolean {
  return !offer.customPricing && offer.yearlyUsd != null;
}

export function purchasePathForOffer(offer: SubscriptionOffer): string {
  if (offer.customPricing) {
    return offer.ctaPath?.startsWith('/subscriptions/contact')
      ? offer.ctaPath
      : `/subscriptions/contact?plan=${offer.id}`;
  }
  return `/subscriptions/purchase?plan=${offer.id}`;
}

export async function fetchSubscriptionOffers(): Promise<SubscriptionOffer[]> {
  try {
    const res = await fetch('/api/subscription-offers');
    const data = await res.json();
    if (res.ok && data?.success && Array.isArray(data.data) && data.data.length) {
      return data.data as SubscriptionOffer[];
    }
  } catch {
    /* fall through */
  }
  return DEFAULT_SUBSCRIPTION_OFFERS;
}
